// Payment terms — the ONE owner (operator ruling 2026-10-20, Batch 4).
//
// Stored form, everywhere (QuickBooks-style): 'due_on_receipt' or 'net_N' with N 1..365.
// Every writer stores toCanonicalPaymentTerms(x); every reader shows paymentTermsLabel(x);
// every due date is issue date + paymentTermsDays(x); every dropdown is built from
// paymentTermsOptions(). The database mirrors toCanonicalPaymentTerms EXACTLY in
// canonical_payment_terms(text) (supabase/migrations/20261020_payment_terms_single_source.sql),
// which a BEFORE INSERT/UPDATE trigger applies to every terms column. Change one, change both.
//
// Until that migration is applied the DB still holds the older spellings (NET30, NET_45,
// 'Net 30', 'Net 60 days', 'Net 0 days', 'Due on Receipt' …), so toCanonicalPaymentTerms
// reads all of them. Anything it cannot read returns null; callers fall back to the company
// default through resolvePaymentTerms(), never to a hard-coded guess.
//
// Before 2026-10-20 there were eight translators (QuoteBuilder, InvoicingSettingsTab, the
// onboarding steps, InvoicesService's NET_xx map + first-number regex, the complete-and-
// invoice popup, the portal, InvoiceTemplate, VendorsService) and they disagreed: the regex
// turned '50% Deposit, Balance on Completion' into Net 50 and 'deposit_required' into due on
// receipt, the quote dropdown had no Net 45, and an invoice printed the raw word.

export const DEFAULT_PAYMENT_TERMS = 'net_30';
export const MAX_NET_DAYS = 365;
export const CUSTOM_PAYMENT_TERMS = 'custom';

// Presets offered in every terms dropdown, in this order, plus Custom (N days).
export const PRESET_PAYMENT_TERMS = ['due_on_receipt', 'net_7', 'net_15', 'net_30', 'net_45', 'net_60', 'net_90'];

// Spellings that all mean "pay now". 'Due on completion' is due on receipt (ruling 3); COD
// and prepaid are the vendor-side spellings of the same thing.
const DUE_NOW = new Set([
  'dueonreceipt', 'duereceipt', 'dueuponreceipt', 'uponreceipt', 'onreceipt', 'receipt',
  'dueoncompletion', 'dueuponcompletion', 'uponcompletion', 'oncompletion',
  'cod', 'cashondelivery', 'prepaid', 'immediate', 'dueimmediately',
]);

// Any stored, legacy or display form -> 'due_on_receipt' | 'net_N' | null.
// null for blank input AND for anything that is not a payment term (e.g. 'deposit_required',
// '50% Deposit, Balance on Completion', free text). Never guesses a number out of prose.
export function toCanonicalPaymentTerms(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim().toLowerCase();
  if (s === '') return null;
  const key = s.replace(/[\s_-]+/g, '');
  if (DUE_NOW.has(key)) return 'due_on_receipt';
  const m = key.match(/^net(\d{1,3})(?:days?)?$/)
    || key.match(/^(\d{1,3})days?$/)
    || key.match(/^2\/?10net(\d{1,3})$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (n === 0) return 'due_on_receipt';
  if (n >= 1 && n <= MAX_NET_DAYS) return `net_${n}`;
  return null;
}

// First candidate that is a real term, else the default. Pass the record's own term first,
// then the company default: resolvePaymentTerms(workOrder.payment_terms, companyDefault).
export function resolvePaymentTerms(...candidates) {
  for (const c of candidates) {
    const t = toCanonicalPaymentTerms(c);
    if (t) return t;
  }
  return DEFAULT_PAYMENT_TERMS;
}

// Days from the issue date to the due date. null when the input is not a term.
export function paymentTermsDays(raw) {
  const t = toCanonicalPaymentTerms(raw);
  if (!t) return null;
  if (t === 'due_on_receipt') return 0;
  return parseInt(t.slice(4), 10);
}

// Customer-facing label: 'Due on receipt' / 'Net 30'. '' when the input is not a term, so a
// caller renders nothing rather than a raw code.
export function paymentTermsLabel(raw) {
  const t = toCanonicalPaymentTerms(raw);
  if (!t) return '';
  if (t === 'due_on_receipt') return 'Due on receipt';
  return `Net ${parseInt(t.slice(4), 10)}`;
}

// Options for a terms dropdown. `current` (any spelling) is added when it is a valid term the
// presets do not cover (e.g. Net 21), so the select always shows what is saved. The Custom
// entry lets the user type N days (1..365).
export function paymentTermsOptions(current) {
  const opts = PRESET_PAYMENT_TERMS.map((value) => ({ value, label: paymentTermsLabel(value) }));
  const cur = toCanonicalPaymentTerms(current);
  if (cur && !PRESET_PAYMENT_TERMS.includes(cur)) {
    const days = paymentTermsDays(cur);
    const at = opts.findIndex((o) => (paymentTermsDays(o.value) || 0) > days);
    opts.splice(at === -1 ? opts.length : at, 0, { value: cur, label: paymentTermsLabel(cur) });
  }
  opts.push({ value: CUSTOM_PAYMENT_TERMS, label: 'Custom (number of days)' });
  return opts;
}

// Custom N days -> canonical. null unless 1..365 (0 is due on receipt — pick that preset).
export function customDaysToPaymentTerms(n) {
  const d = parseInt(n, 10);
  if (!Number.isFinite(d) || d < 0 || d > MAX_NET_DAYS) return null;
  return d === 0 ? 'due_on_receipt' : `net_${d}`;
}
